import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Heart, MessageCircle, Share2, Music, Volume2, VolumeX,
  Play, Pause, Loader2, ChevronUp, ChevronDown, UserPlus, Plus,
  X, Send, Video, Sparkles
} from 'lucide-react';
import { PlayLogo } from '../components/Logo';
import { API, API_BASE_URL } from '../config/api';
import UserAvatar from '../components/UserAvatar';
import { canComment, formatRemainingComments } from '../utils/permissions';
import { getProfileUrl, getDisplayHandle } from '../utils/profileUtils';
import { safeJson } from '../utils/safeJson';
import { lock, unlock } from '../utils/scrollLock';
import CommentActionsMenu, { HiddenCommentPlaceholder } from '../components/CommentActionsMenu';

// Helper to check if comment should be visible to current user
const isCommentVisible = (comment, reel, user) => {
  if (!comment.is_hidden) return true;
  // Hidden comments visible to content owner, platform owner, or admin
  const isContentOwner = user?.user_id === reel?.user_id;
  const isPlatformOwner = user?.email === 'itzchopbusiness1@gmail.com';
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  return isContentOwner || isPlatformOwner || isAdmin;
};

export default function ReelsPage() {
  const { user, isAuthenticated, loading: authLoading, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [reels, setReels] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    fetchReels();
  }, []);

  const fetchReels = async () => {
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const res = await fetch(`${API}/reels?limit=20`, { headers });
      
      if (res.ok) {
        const data = await res.json();
        setReels(data.reels || []);
      }
    } catch (err) {
      console.error('Reels fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const goToNext = () => {
    if (currentIndex < reels.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const goToPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  // Handle "Create Clip" button click
  const handleCreateReel = () => {
    if (!isAuthenticated) {
      toast.info('Please sign in to create a clip');
      navigate('/login');
      return;
    }
    navigate('/reels/create');
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'j') {
        goToNext();
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        goToPrev();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, reels.length]);

  // Handle scroll/swipe - ONLY on the video area, not action buttons
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let startY = 0;
    let startX = 0;
    
    const handleTouchStart = (e) => {
      // Don't capture touches that start on action buttons
      const target = e.target;
      if (target.closest('[data-action-button]') || target.closest('.action-buttons-container')) {
        return;
      }
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
      // Don't process if touch started on action buttons
      const target = e.target;
      if (target.closest('[data-action-button]') || target.closest('.action-buttons-container')) {
        return;
      }
      
      const endY = e.changedTouches[0].clientY;
      const endX = e.changedTouches[0].clientX;
      const diffY = startY - endY;
      const diffX = startX - endX;
      
      // Only trigger swipe if vertical movement is greater than horizontal (avoid accidental swipes)
      if (Math.abs(diffY) > 50 && Math.abs(diffY) > Math.abs(diffX)) {
        if (diffY > 0) goToNext();
        else goToPrev();
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [currentIndex, reels.length]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (reels.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black px-4">
        <div className="text-center">
          <PlayLogo size="lg" className="mx-auto mb-4" />
          <h2 className="font-heading text-xl font-bold mb-2">No Clips Yet</h2>
          <p className="text-muted-foreground mb-6">Be the first to post a Soundstage Clip!</p>
          <Button 
            onClick={handleCreateReel}
            data-testid="create-reel-empty-btn"
          >
            Create Clip
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="h-screen bg-black overflow-hidden" 
      data-testid="reels-page"
    >
      {/* Create Clip FAB */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleCreateReel();
        }}
        onTouchStart={(e) => e.stopPropagation()}
        className="fixed bottom-24 right-4 z-30 w-14 h-14 rounded-full bg-secondary hover:bg-secondary/80 flex items-center justify-center shadow-lg transition-all active:scale-95"
        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
        data-testid="create-reel-fab"
        data-action-button="true"
      >
        <Plus className="w-7 h-7 text-black" />
      </button>

      {/* Navigation arrows */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-2">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            goToPrev();
          }}
          onTouchStart={(e) => e.stopPropagation()}
          disabled={currentIndex === 0}
          className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center disabled:opacity-30"
          data-action-button="true"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            goToNext();
          }}
          onTouchStart={(e) => e.stopPropagation()}
          disabled={currentIndex === reels.length - 1}
          className="w-10 h-10 rounded-full bg-muted/50 flex items-center justify-center disabled:opacity-30"
          data-action-button="true"
        >
          <ChevronDown className="w-6 h-6" />
        </button>
      </div>

      {/* Reels container */}
      <div 
        className="h-full transition-transform duration-300"
        style={{ transform: `translateY(-${currentIndex * 100}%)` }}
      >
        {reels.map((reel, index) => (
          <ReelItem 
            key={reel.reel_id} 
            reel={reel} 
            isActive={index === currentIndex}
            isAuthenticated={isAuthenticated}
            getAuthHeaders={getAuthHeaders}
            user={user}
          />
        ))}
      </div>

      {/* Progress indicator */}
      <div className="absolute top-4 left-0 right-0 flex justify-center gap-1 z-20 pointer-events-none">
        {reels.map((_, idx) => (
          <div
            key={idx}
            className={`h-1 rounded-full transition-all ${
              idx === currentIndex ? 'w-8 bg-white' : 'w-1 bg-muted/70'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function ReelItem({ reel, isActive, isAuthenticated, getAuthHeaders, user }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [liked, setLiked] = useState(reel.liked_by_me || false);
  const [likeCount, setLikeCount] = useState(reel.likes || 0);
  const [isFollowing, setIsFollowing] = useState(reel.is_following || false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [videoError, setVideoError] = useState(null); // null | 'temporary' | 'permanent'
  const [videoLoading, setVideoLoading] = useState(true);
  const [commentStatus, setCommentStatus] = useState(null);
  
  // Store the playback time before opening comments
  const playbackTimeRef = useRef(0);
  const wasPlayingRef = useRef(false);

  // Manage scroll lock for comments panel
  useEffect(() => {
    if (showComments) {
      lock();
    }
    return () => {
      if (showComments) {
        unlock();
      }
    };
  }, [showComments]);

  // Construct full video URL
  const videoUrl = reel.video_url?.startsWith('/') 
    ? `${API_BASE_URL}${reel.video_url}` 
    : reel.video_url;

  // Pre-set permanent error for reels flagged as unavailable by backend
  useEffect(() => {
    if (reel.video_unavailable) {
      setVideoError('permanent');
      setVideoLoading(false);
    }
  }, [reel.video_unavailable]);

  // Fetch comment status when authenticated
  useEffect(() => {
    if (isAuthenticated && isActive) {
      fetchCommentStatus();
    }
  }, [isAuthenticated, isActive]);

  const fetchCommentStatus = async () => {
    try {
      const res = await fetch(`${API}/me/comment-status`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setCommentStatus(data);
      }
    } catch (err) {
      console.error('Error fetching comment status:', err);
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      if (isActive && !showComments && !videoError) {
        videoRef.current.play().catch(() => {});
        setPlaying(true);
      } else if (!isActive) {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
        setPlaying(false);
      }
    }
  }, [isActive, videoError]);

  const retryCountRef = useRef(0);
  const MAX_RETRIES = 2;

  const handleVideoError = (e) => {
    const err = e.target?.error;
    const code = err?.code || 0;
    const msg = err?.message || 'Unknown error';
    console.error('Video error:', JSON.stringify({ code, msg, src: videoUrl }));
    setVideoLoading(false);
    if (retryCountRef.current < MAX_RETRIES) {
      retryCountRef.current += 1;
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.load();
        }
      }, 500 * retryCountRef.current);
    } else {
      // Only permanent after exhausting retries
      setVideoError(code === 4 ? 'permanent' : 'temporary');
    }
  };

  const handleRetryVideo = () => {
    retryCountRef.current = 0;
    setVideoError(null);
    setVideoLoading(true);
    if (videoRef.current) {
      videoRef.current.load();
    }
  };

  const handleVideoLoaded = () => {
    setVideoLoading(false);
    setVideoError(null);
  };

  const togglePlay = (e) => {
    // Only toggle play if clicking on the video itself
    if (e.target !== videoRef.current) return;
    
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
      setPlaying(!playing);
    }
  };

  // Handle opening comments - CRITICAL: Preserve video state
  const handleOpenComments = (e) => {
    // Prevent ALL event propagation - critical for mobile
    e.preventDefault();
    e.stopPropagation();
    
    // Save current playback state
    if (videoRef.current) {
      playbackTimeRef.current = videoRef.current.currentTime;
      wasPlayingRef.current = !videoRef.current.paused;
      // Pause the video while comments are open
      videoRef.current.pause();
      setPlaying(false);
    }
    
    setShowComments(true);
    fetchComments();
  };

  // Handle closing comments - restore video state
  const handleCloseComments = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setShowComments(false);
    
    // Restore playback state
    if (videoRef.current) {
      videoRef.current.currentTime = playbackTimeRef.current;
      if (wasPlayingRef.current) {
        videoRef.current.play().catch(() => {});
        setPlaying(true);
      }
    }
  };

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const res = await fetch(`${API}/reels/${reel.reel_id}/comments`, { headers });
      if (res.ok) {
        const data = await res.json();
        setComments(data.comments || []);
      }
    } catch (err) {
      console.error('Comments fetch error:', err);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!commentText.trim()) return;
    
    if (!isAuthenticated) {
      toast.info('Please sign in to comment');
      return;
    }
    
    // Check permission
    const commentPerm = canComment(user, commentStatus);
    if (!commentPerm.allowed) {
      if (commentPerm.rateLimited && commentStatus?.remaining === 0) {
        toast.error('Comment limit reached. Upgrade to Pro for unlimited comments.');
      } else {
        toast.error(commentPerm.reason || 'You cannot comment at this time');
      }
      return;
    }
    
    setSubmittingComment(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const res = await fetch(`${API}/reels/${reel.reel_id}/comments`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ text: commentText.trim() })
      });
      
      if (res.ok) {
        const newComment = await res.json();
        setComments(prev => [newComment, ...prev]);
        setCommentText('');
        toast.success('Comment added!');
        // Refresh comment status
        fetchCommentStatus();
      } else if (res.status === 429) {
        const error = await res.json();
        toast.error(error.detail || 'Comment limit reached. Upgrade to Pro for unlimited comments.');
        fetchCommentStatus();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to add comment');
      }
    } catch (err) {
      console.error('Comment error:', err);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Handle unhiding a comment
  const handleUnhideComment = async (commentId) => {
    try {
      const res = await fetch(`${API}/reel-comments/${commentId}/hide`, {
        method: 'PATCH',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        setComments(prev => prev.map(c => 
          c.comment_id === commentId ? {...c, is_hidden: false} : c
        ));
        toast.success('Comment is now visible');
      }
    } catch (err) {
      toast.error('Failed to unhide comment');
    }
  };

  const handleLike = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) {
      toast.info('Please sign in to like');
      return;
    }
    
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const endpoint = liked ? 'unlike' : 'like';
      await fetch(`${API_BASE_URL}/api/reels/${reel.reel_id}/${endpoint}`, {
        method: 'POST',
        headers
      });
      setLiked(!liked);
      setLikeCount(prev => liked ? prev - 1 : prev + 1);
    } catch (err) {
      console.error('Like error:', err);
    }
  };

  const handleFollow = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!isAuthenticated) return;
    
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      const endpoint = isFollowing ? 'unfollow' : 'follow';
      await fetch(`${API_BASE_URL}/api/users/${reel.user_id}/${endpoint}`, {
        method: 'POST',
        headers
      });
      setIsFollowing(!isFollowing);
    } catch (err) {
      console.error('Follow error:', err);
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const shareUrl = `${window.location.origin}/reels/${reel.reel_id}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `Clip by @${reel.handle}`,
          url: shareUrl
        });
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied!');
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.message?.includes('cancel')) {
        return;
      }
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied!');
      } catch {
        console.error('Share failed:', error);
      }
    }
  };

  const handleMuteToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMuted(!muted);
  };

  return (
    <div className="h-screen relative flex items-center justify-center bg-black">
      {/* Video loading indicator */}
      {videoLoading && !videoError && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-muted-foreground/70" />
        </div>
      )}

      {/* Video error fallback */}
      {videoError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mb-4">
            <Video className="w-8 h-8 text-muted-foreground/70" />
          </div>
          {videoError === 'permanent' ? (
            <>
              <p className="text-muted-foreground text-sm mb-2">Clip no longer available</p>
              <p className="text-muted-foreground/60 text-xs mb-4">This video file has been removed or is no longer accessible</p>
            </>
          ) : (
            <>
              <p className="text-muted-foreground text-sm mb-2">Video unavailable</p>
              <p className="text-muted-foreground/60 text-xs mb-4">Temporary issue loading this video</p>
              <button 
                onClick={(e) => { e.stopPropagation(); handleRetryVideo(); }}
                className="px-4 py-2 rounded-full bg-muted/50 hover:bg-muted/60 text-foreground text-sm transition-colors"
                data-testid="retry-video-btn"
              >
                Tap to Retry
              </button>
            </>
          )}
        </div>
      )}

      {/* Video - click handler only on the video element itself */}
      <video
        ref={videoRef}
        src={videoUrl}
        className={`h-full max-w-full object-contain cursor-pointer ${videoError ? 'hidden' : ''}`}
        loop
        muted={muted}
        playsInline
        webkit-playsinline=""
        preload="metadata"
        onClick={togglePlay}
        onError={handleVideoError}
        onLoadedData={handleVideoLoaded}
        onCanPlay={handleVideoLoaded}
        data-testid="reel-video"
      />

      {/* Play/Pause overlay - pointer-events-none so it doesn't intercept clicks */}
      {!playing && !showComments && !videoError && (
        <div 
          className="absolute inset-0 flex items-center justify-center pointer-events-none"
          data-testid="play-overlay"
        >
          <div className="w-20 h-20 rounded-full bg-black/30 flex items-center justify-center">
            <Play className="w-10 h-10 text-white" />
          </div>
        </div>
      )}

      {/* Sidebar actions - HIGH Z-INDEX and proper event handling */}
      <div 
        className="action-buttons-container absolute right-4 bottom-24 flex flex-col items-center gap-6 z-30"
        style={{ pointerEvents: 'auto' }}
      >
        {/* Author avatar */}
        <div className="relative" data-action-button="true">
          <a 
            href={getProfileUrl({ handle: reel.handle, user_id: reel.user_id })} 
            className="block w-12 h-12 rounded-full bg-muted/60 overflow-hidden border-2 border-white"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {reel.avatar ? (
              <img src={reel.avatar} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-bold">
                {reel.name?.charAt(0)}
              </div>
            )}
          </a>
          {!isFollowing && isAuthenticated && (
            <button
              type="button"
              onClick={handleFollow}
              onTouchStart={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-secondary flex items-center justify-center"
              style={{ touchAction: 'manipulation' }}
              data-action-button="true"
            >
              <UserPlus className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Like */}
        <button 
          type="button"
          onClick={handleLike} 
          onTouchStart={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="flex flex-col items-center"
          style={{ touchAction: 'manipulation' }}
          data-action-button="true"
          data-testid="reel-like-btn"
        >
          <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center active:scale-90 transition-transform">
            <Heart className={`w-6 h-6 ${liked ? 'fill-red-500 text-red-500' : 'text-white'}`} />
          </div>
          <span className="text-xs mt-1">{likeCount}</span>
        </button>

        {/* Comment - CRITICAL: Opens panel, does NOT navigate */}
        <button 
          type="button"
          onClick={handleOpenComments}
          onTouchStart={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="flex flex-col items-center"
          style={{ touchAction: 'manipulation' }}
          data-action-button="true"
          data-testid="reel-comment-btn"
        >
          <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center active:scale-90 transition-transform">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <span className="text-xs mt-1">{reel.comment_count || 0}</span>
        </button>

        {/* Share */}
        <button 
          type="button"
          onClick={handleShare} 
          onTouchStart={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="flex flex-col items-center"
          style={{ touchAction: 'manipulation' }}
          data-action-button="true"
          data-testid="reel-share-btn"
        >
          <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center active:scale-90 transition-transform">
            <Share2 className="w-6 h-6 text-white" />
          </div>
          <span className="text-xs mt-1">Share</span>
        </button>

        {/* Mute toggle */}
        <button 
          type="button"
          onClick={handleMuteToggle} 
          onTouchStart={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className="flex flex-col items-center"
          style={{ touchAction: 'manipulation' }}
          data-action-button="true"
          data-testid="reel-mute-btn"
        >
          <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center active:scale-90 transition-transform">
            {muted ? <VolumeX className="w-6 h-6 text-white" /> : <Volume2 className="w-6 h-6 text-white" />}
          </div>
        </button>
      </div>

      {/* Bottom info */}
      <div className="absolute bottom-4 left-4 right-20 z-10 pointer-events-none">
        <a 
          href={getProfileUrl({ handle: reel.handle, user_id: reel.user_id })} 
          className="font-bold hover:underline pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {getDisplayHandle({ handle: reel.handle, name: reel.name })}
        </a>
        {reel.caption && (
          <p className="text-sm mt-1 line-clamp-2">{reel.caption}</p>
        )}
        {reel.audio_name && (
          <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
            <Music className="w-4 h-4" />
            <span className="truncate">{reel.audio_name}</span>
          </div>
        )}
      </div>

      {/* Comments Panel - Bottom Sheet */}
      {showComments && (
        <div 
          className="fixed inset-0 z-50"
          onClick={handleCloseComments}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-background/60" />
          
          {/* Comments Sheet */}
          <div 
            className="absolute bottom-0 left-0 right-0 bg-background rounded-t-2xl max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            data-testid="comments-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="font-bold">Comments ({reel.comment_count || 0})</h3>
              <button 
                type="button"
                onClick={handleCloseComments}
                onTouchStart={(e) => e.stopPropagation()}
                className="w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loadingComments ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No comments yet</p>
                  <p className="text-sm">Be the first to comment!</p>
                </div>
              ) : (
                comments.filter(c => !c.is_deleted).map((comment) => (
                  comment.is_hidden && !isCommentVisible(comment, reel, user) ? (
                    <HiddenCommentPlaceholder 
                      key={comment.comment_id}
                      comment={comment}
                      onUnhide={() => handleUnhideComment(comment.comment_id)}
                    />
                  ) : (
                    <div key={comment.comment_id} className="flex gap-3 group">
                      <UserAvatar 
                        src={comment.author_avatar}
                        alt={comment.author_name}
                        size="sm"
                        fallbackInitial={comment.author_name}
                      />
                      <div className="flex-1">
                        <div className="flex items-baseline gap-2">
                          <span className="font-medium text-sm">@{comment.author_handle}</span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(comment.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm mt-1">{comment.content}</p>
                      </div>
                      <CommentActionsMenu
                        comment={comment}
                        contentOwnerId={reel?.user_id}
                        contentType="reel"
                        onDelete={(commentId) => {
                          setComments(prev => prev.filter(c => c.comment_id !== commentId));
                        }}
                        onHide={(commentId, hidden) => {
                          setComments(prev => prev.map(c => 
                            c.comment_id === commentId ? {...c, is_hidden: hidden} : c
                          ));
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                  )
                ))
              )}
            </div>

            {/* Comment Input */}
            {isAuthenticated ? (
              <form 
                onSubmit={handleSubmitComment}
                className="p-4 border-t border-border flex gap-2"
              >
                <Input
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1"
                  disabled={submittingComment}
                  data-testid="comment-input"
                />
                <Button 
                  type="submit" 
                  size="icon"
                  disabled={!commentText.trim() || submittingComment || (commentStatus?.rate_limited && commentStatus?.remaining === 0)}
                  data-testid="submit-comment-btn"
                >
                  {submittingComment ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </form>
            ) : (
              <div className="p-4 border-t border-border text-center text-muted-foreground text-sm">
                Sign in to comment
              </div>
            )}

            {/* Rate limit indicator for Free artists */}
            {isAuthenticated && commentStatus?.rate_limited && (
              <div className="px-4 pb-2 flex items-center justify-between text-xs">
                {commentStatus.remaining > 0 ? (
                  <span className="text-muted-foreground">
                    {formatRemainingComments(commentStatus.remaining, commentStatus.limit)}
                  </span>
                ) : (
                  <span className="text-amber-500">
                    Comment limit reached
                  </span>
                )}
                <button 
                  className="flex items-center gap-1 text-primary hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = '/subscription';
                  }}
                >
                  <Sparkles className="w-3 h-3" />
                  Upgrade for unlimited
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
